import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil, GripVertical, RotateCcw, X, Plus, Check, ClipboardList } from 'lucide-react';
// react-grid-layout v2 splits the legacy class-component API (which still
// exports WidthProvider + the default ReactGridLayout) into the /legacy
// subpath. The new hook-based API at the top level doesn't ship
// WidthProvider; keep the legacy import so the Edit-Dashboard mode
// continues to drag/drop without rewriting against useGridLayout.
import { Responsive as ResponsiveGrid, WidthProvider } from 'react-grid-layout/legacy';
import { useFacility } from '../context/FacilityContext';
import { useAuth } from '../context/AuthContext';
import dashboardService from '../services/dashboardService';
import facilitiesService from '../services/facilitiesService';
import apiClient from '../utils/apiClient';
import MetricDrillDown from '../components/MetricDrillDown';
import { formatRowStamp } from '../utils/datetime';
import {
  RevenueBarWidget,
  AgingDonutWidget,
  OccupancyGaugeWidget,
  KeyMetricsWidget,
  OccupancyTrendWidget,
} from '../components/dashboard/DashboardCharts';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, Tooltip,
} from 'recharts';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

// CANONICAL editorial dashboard (Documentation/ESS/CANONICAL_design_spec.md).
// Numbers are the interface. Monochrome chart. Semantic color ONLY on
// delta pills / activity dots. Inter 400/500 — no bold. Flat: no shadow.
// Reference the global theme tokens (index.css :root / html.dark) so
// the dashboard follows light/dark instead of being frozen by inline
// hex values. SURFACE/BG added for cards + chart tooltip.
const INK = 'var(--c-ink)';
const INK2 = 'var(--c-ink2)';
const INK3 = 'var(--c-ink3)';
const SUCCESS = 'var(--c-success)';
const DANGER = 'var(--c-danger)';
const WARNING = 'var(--c-warning)';
const INFO = 'var(--c-info)';
const SURFACE = 'var(--c-surface)';
const BG = 'var(--c-bg)';
const BORDER = 'var(--c-border)';
const CHART_LINE = 'var(--c-ink3)';
// Distinct categorical colors for the per-unit-type occupancy bars (cycled).
// Fixed hues (deliberately NOT blue-600/indigo-600, so the brand-color CSS
// hijack leaves them alone) — they're semantic-by-category, not brand-tinted.
const TYPE_COLORS = ['#0EA5E9', '#22C55E', '#F59E0B', '#A855F7', '#EF4444',
  '#14B8A6', '#EC4899', '#84CC16'];

const tabular = { fontVariantNumeric: 'tabular-nums' };
const fmtMoney = (n) =>
  n == null ? '$0' : new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(n);

const TT = {
  contentStyle: {
    borderRadius: 8, border: `0.5px solid ${BORDER}`,
    boxShadow: 'none', fontSize: 12, padding: '6px 10px',
    background: SURFACE,
  },
  labelStyle: { color: INK, fontWeight: 500, marginBottom: 2 },
  itemStyle: { color: INK2 },
};

// Drill-down wrapper. Renders as <Link> when `to` is given, else a plain
// div. Adds cursor-pointer + hover affordance + transition so the tile/row
// reads as actionable. role="button" + aria-label keep it accessible for
// screen-reader users navigating the dashboard.
const Drill = ({ to, ariaLabel, onClick, children, className = '', style }) => {
  const interactive = !!(to || onClick);
  if (!interactive) {
    return <div className={className} style={style}>{children}</div>;
  }
  const baseClass = `cursor-pointer transition-colors hover:bg-slate-50 ${className}`;
  if (to) {
    return (
      <Link
        to={to}
        aria-label={ariaLabel}
        className={baseClass}
        style={{ display: 'block', ...style }}
      >
        {children}
      </Link>
    );
  }
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(e);
        }
      }}
      className={baseClass}
      style={style}
    >
      {children}
    </div>
  );
};

// Floating metric — NO card/bg/border (spec §Metrics). When `to` is set
// the entire tile becomes a Link; when `onClick` is set it becomes a button
// (used to open the drill-down modal). Both add hover + cursor affordance.
const Metric = ({ label, value, deltaPct, deltaTone, sub, to, onClick, ariaLabel }) => {
  const body = (
    <div style={{ borderRadius: 8, padding: 4, margin: -4 }}>
      <p style={{ fontSize: 11, color: INK3, letterSpacing: '0.2px' }}>
        {label}
      </p>
      <p
        className="mt-2"
        style={{ fontSize: 36, fontWeight: 500, letterSpacing: '-1.2px',
          color: INK, lineHeight: 1, ...tabular }}
      >
        {value}
      </p>
      <div className="mt-1.5 flex items-baseline gap-2" style={tabular}>
        {deltaPct != null && (
          <span
            style={{ fontSize: 11, fontWeight: 500,
              color: deltaTone === 'danger' ? DANGER
                : deltaTone === 'warning' ? WARNING : SUCCESS }}
          >
            {deltaPct >= 0 ? '↑' : '↓'}{Math.abs(deltaPct)}%
          </span>
        )}
        {sub && (
          <span style={{ fontSize: 11, color: INK3 }}>{sub}</span>
        )}
      </div>
    </div>
  );
  if (!to && !onClick) return body;
  return (
    <Drill to={to} onClick={onClick} ariaLabel={ariaLabel || label}>
      {body}
    </Drill>
  );
};

// White card per spec: 0.5px border, r12, NO shadow. Flex column so
// content fills card height (charts) and overflow scrolls internally.
const Card = ({ title, chip, children, className = '', headerExtra }) => (
  <div
    className={`flex flex-col min-h-0 h-full ${className}`}
    style={{ background: SURFACE, border: `0.5px solid ${BORDER}`,
      borderRadius: 12, padding: 16 }}
  >
    <div
      className="flex items-center gap-2 flex-shrink-0"
      style={{ marginBottom: 10 }}
    >
      <h2 style={{ fontSize: 13, fontWeight: 500, color: INK }}>{title}</h2>
      {chip && (
        <span
          style={{ fontSize: 10, color: INK3, background: BG,
            borderRadius: 10, padding: '2px 8px' }}
        >
          {chip}
        </span>
      )}
      <div className="flex-1" />
      {headerExtra}
    </div>
    <div className="flex-1 min-h-0 overflow-y-auto sidebar-scroll">
      {children}
    </div>
  </div>
);

// Occupancy/attribution bar row. Wrapped in Drill when `to` set.
const BarRow = ({ label, pct, fill, trail, labelW = 50, trailW = 28,
  to, ariaLabel }) => {
  const body = (
    <div className="flex items-center gap-3" style={{ paddingBlock: 3 }}>
      <span style={{ fontSize: 11, color: INK3, width: labelW, flexShrink: 0 }}>
        {label}
      </span>
      <div
        className="flex-1 overflow-hidden"
        style={{ height: 4, background: BG, borderRadius: 2 }}
      >
        <div
          style={{ height: '100%', borderRadius: 2,
            width: `${Math.min(100, Math.max(0, pct))}%`,
            background: fill || INK2 }}
        />
      </div>
      <span
        className="text-right"
        style={{ fontSize: trail ? 10 : 12, fontWeight: 500, color: INK,
          width: trail ? 80 : trailW, flexShrink: 0, ...tabular }}
      >
        {trail ?? `${Math.round(pct)}%`}
      </span>
    </div>
  );
  if (!to) return body;
  return (
    <Drill to={to} ariaLabel={ariaLabel || label}>{body}</Drill>
  );
};

const KpiRow = ({ k, v, to, ariaLabel }) => {
  const body = (
    <div
      className="flex justify-between items-center"
      style={{ paddingBlock: 4 }}
    >
      <span style={{ fontSize: 11, color: INK3 }}>{k}</span>
      <span style={{ fontSize: 11, fontWeight: 500, color: INK, ...tabular }}>
        {v}
      </span>
    </div>
  );
  if (!to) return body;
  return <Drill to={to} ariaLabel={ariaLabel || k}>{body}</Drill>;
};

const DOT = { success: SUCCESS, danger: DANGER, info: INFO,
  warning: WARNING, tertiary: INK3 };

// Responsive (breakpoint-aware), NOT the static single-breakpoint GridLayout.
// The old comment claimed "the dashboard already has its own internal
// responsiveness via Tailwind classes" — that premise was wrong. RGL positions
// every widget ABSOLUTELY against a fixed column count, so cols={12} on a
// 390px phone gave a w:4 tile ~100px and Tailwind inside the widget could do
// nothing about it: labels overlapped their own values and donuts clipped.
// Responsive reflows the COLUMN COUNT per breakpoint, so widgets stack instead
// of being crushed.
const ResponsiveGridLayout = WidthProvider(ResponsiveGrid);

// Breakpoints keyed to the real failure: below ~700px a half-width chart is
// too narrow for axis labels, so drop to a single column and let widgets
// stack. RGL maps a layout per breakpoint; we generate the narrow ones from
// the operator's saved 12-col layout so their arrangement is preserved.
const GRID_BREAKPOINTS = { lg: 1100, md: 700, sm: 0 };
const GRID_COLS = { lg: 12, md: 6, sm: 1 };

// Derive a narrower arrangement from the 12-col layout: keep reading order
// (top-to-bottom, left-to-right) and stack.
const stackLayout = (base, cols) => {
  const ordered = [...base].sort((a, b) => (a.y - b.y) || (a.x - b.x));
  let y = 0;
  return ordered.map((item) => {
    const w = cols === 1 ? 1 : Math.min(cols, item.w >= 6 ? 6 : 3);
    const placed = { ...item, x: 0, y, w, minW: Math.min(item.minW || 1, w) };
    y += item.h;
    return placed;
  });
};

// Slug each activity item to a sensible drill-down target. Returns null
// if we can't safely route — caller renders an inert row.
const activityRoute = (a) => {
  if (!a) return null;
  // Prefer an explicit URL the backend gave us.
  if (typeof a.href === 'string' && a.href) return a.href;
  if (typeof a.url === 'string' && a.url) return a.url;
  const type = (a.type || a.entity || '').toString().toLowerCase();
  const id = a.entity_id || a.target_id || a.id;
  if (type.includes('rental')) return id ? `/rentals/${id}/edit` : '/rentals';
  if (type.includes('payment')) return id ? `/payments` : '/payments';
  if (type.includes('lead')) return '/reservations?tab=leads';
  if (type.includes('invoice')) return id ? `/invoices` : '/invoices';
  if (type.includes('customer') || type.includes('tenant')) {
    return id ? `/customers/${id}/edit` : '/customers';
  }
  if (type.includes('unit')) return '/units';
  return null;
};

// ── Widget registry — single source of truth ─────────────────────────────
// Every widget the operator can place on the dashboard is declared here once:
// its id (matches the react-grid-layout `i` AND the key in the `widgets`
// render map), a human label (shown in the Add-widget menu), default size, and
// minimum size. Adding a new widget = add a registry entry + a renderer keyed
// by the same id. The Add/Remove UX, the default layout, and the back-compat
// guard all derive from this list, so they never drift.
const WIDGET_REGISTRY = [
  // — Hero KPI tiles —
  { id: 'kpi-revenue', label: 'Revenue this month',
    defaultW: 3, defaultH: 2, minW: 2, minH: 2 },
  { id: 'kpi-occupancy', label: 'Occupancy',
    defaultW: 3, defaultH: 2, minW: 2, minH: 2 },
  { id: 'kpi-past-due', label: 'Past due',
    defaultW: 3, defaultH: 2, minW: 2, minH: 2 },
  { id: 'kpi-leads', label: 'Leads this month',
    defaultW: 3, defaultH: 2, minW: 2, minH: 2 },
  // — New ESS-style operational KPI tiles —
  { id: 'kpi-move-ins', label: 'Move-ins (this month)',
    defaultW: 3, defaultH: 2, minW: 2, minH: 2 },
  { id: 'kpi-move-outs', label: 'Move-outs (this month)',
    defaultW: 3, defaultH: 2, minW: 2, minH: 2 },
  { id: 'kpi-waiting-list', label: 'Waiting list',
    defaultW: 3, defaultH: 2, minW: 2, minH: 2 },
  { id: 'kpi-website-rentals', label: '% Website rentals',
    defaultW: 3, defaultH: 2, minW: 2, minH: 2 },
  { id: 'kpi-recurring-billing', label: '% Recurring billing',
    defaultW: 3, defaultH: 2, minW: 2, minH: 2 },
  { id: 'kpi-active-rentals', label: 'Active rentals',
    defaultW: 3, defaultH: 2, minW: 2, minH: 2 },
  // — Standalone move-in / move-out KPI pair (pulled out of Activity) —
  { id: 'kpi-move-inout', label: 'Move-ins / Move-outs',
    defaultW: 4, defaultH: 2, minW: 3, minH: 2 },
  // — Charts (now first-class grid widgets) —
  { id: 'chart-revenue-bar', label: 'Revenue trend (12 months)',
    defaultW: 8, defaultH: 6, minW: 4, minH: 5 },
  { id: 'chart-aging-donut', label: 'Aging donut',
    defaultW: 6, defaultH: 6, minW: 4, minH: 5 },
  { id: 'chart-occupancy-gauge', label: 'Occupancy gauge',
    defaultW: 4, defaultH: 6, minW: 3, minH: 5 },
  { id: 'chart-key-metrics', label: 'Key metrics (unit mix)',
    defaultW: 4, defaultH: 6, minW: 3, minH: 5 },
  { id: 'chart-occupancy-trend', label: 'Occupancy trend (12 months)',
    defaultW: 8, defaultH: 6, minW: 4, minH: 5 },
  // — Panels —
  { id: 'revenue-chart', label: 'Revenue trend (6 months)',
    defaultW: 8, defaultH: 6, minW: 4, minH: 4 },
  { id: 'activity', label: 'Activity feed',
    defaultW: 4, defaultH: 6, minW: 3, minH: 4 },
  { id: 'occupancy-breakdown', label: 'Occupancy breakdown',
    defaultW: 4, defaultH: 6, minW: 3, minH: 4 },
  { id: 'attribution', label: 'Attribution',
    defaultW: 4, defaultH: 6, minW: 3, minH: 4 },
  { id: 'ai-agent', label: 'AI agent',
    defaultW: 4, defaultH: 6, minW: 3, minH: 4 },
];

// Quick id -> registry-entry lookup.
const WIDGET_BY_ID = WIDGET_REGISTRY.reduce((m, w) => {
  m[w.id] = w;
  return m;
}, {});

// Default grid (12 cols). The operator-requested two-row analytics view that
// fits on one screen without scrolling:
//   Row 1 — Payment-due aging donut | Occupancy gauge | Occupancy breakdown
//   Row 2 — Revenue trend (with MoM/YoY comparison footer) | Occupancy trend
//           (with this month's move-ins/outs in its footer)
// Both rows are short (h5) so the whole dashboard is crisp on load with no
// scrollbar. Every other widget (KPI tiles, Key-Metrics donut, Activity,
// Attribution, AI agent, …) still lives in WIDGET_REGISTRY and can be added
// back via Edit → Add widget, or baked into a facility's staff default.
const DEFAULT_LAYOUT = [
  // Row 1 — three analytics tiles. Taller (h6) so the donuts read full-size.
  { i: 'chart-aging-donut', x: 0, y: 0, w: 4, h: 6, minW: 3, minH: 4 },
  { i: 'chart-occupancy-gauge', x: 4, y: 0, w: 4, h: 6, minW: 3, minH: 4 },
  { i: 'occupancy-breakdown', x: 8, y: 0, w: 4, h: 6, minW: 3, minH: 4 },
  // Row 2 — the two trend charts, full width beneath row 1. Revenue carries
  // the MoM/YoY comparison; Occupancy trend carries the move-ins/outs.
  { i: 'chart-revenue-bar', x: 0, y: 6, w: 6, h: 5, minW: 4, minH: 5 },
  { i: 'chart-occupancy-trend', x: 6, y: 6, w: 6, h: 5, minW: 4, minH: 5 },
];

const LS_KEY = 'mysms360.dashboardLayout';
const LS_VERSION_KEY = 'mysms360.dashboardLayoutVersion';

// Bump whenever DEFAULT_LAYOUT changes so existing operators are migrated onto
// the new product-default arrangement. A layout saved before this version is
// considered stale and is superseded once by DEFAULT_LAYOUT; the version is
// stamped the next time the operator saves an edit, after which their own
// customizations are respected again.
//   v2 — hero analytics band: Key-Metrics donut + Occupancy-by-type + the
//        move-ins/outs pair top-left, Revenue trend beside Occupancy trend
//        below, Awaiting-payment over the Occupancy gauge on the right.
//   v3 — compact two-row view: Awaiting-payment | Occupancy | Occupancy
//        breakdown on top (short tiles), Revenue + Occupancy trend beneath.
//        Fits one screen, no scroll.
//   v4 — both rows short (h5); move-ins/outs moved out of the breakdown tile
//        into its own compact tile on row 2; donuts fill their cell.
//   v5 — move-ins/outs folded into the Occupancy-trend footer (left); the two
//        trend charts go full width again; donuts widened.
//   v6 — row 1 tiles taller (h6) so the donuts render full-size.
const DASHBOARD_LAYOUT_VERSION = 6;

// Build a layout item for a registry widget when (re)adding it to the grid.
const layoutItemFor = (id, x = 0, y = Infinity) => {
  const w = WIDGET_BY_ID[id];
  if (!w) return null;
  return {
    i: id, x, y,
    w: w.defaultW, h: w.defaultH,
    minW: w.minW, minH: w.minH,
  };
};

const Dashboard = () => {
  const { facilityId, currentFacility, loading: facilityLoading } = useFacility();
  const { user, isManager } = useAuth();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [rev6, setRev6] = useState([]);
  const [aging, setAging] = useState(null);
  const [occByType, setOccByType] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which KPI drill-down is open (null = none). Each hero metric opens a
  // shared <MetricDrillDown> listing the rows behind the headline number.
  const [drillMetric, setDrillMetric] = useState(null);
  // Revenue-history bar clicks drill a SPECIFIC month; every other opener
  // leaves this null (= the current month). Cleared with the modal so a
  // later KPI click can't inherit a stale month.
  const [drillMonth, setDrillMonth] = useState(null);
  // Open system-task count for the slim footer link (owner 2026-08-06:
  // a bottom link to the task list, WITHOUT making the home page scroll
  // — the footer is flex-shrink-0 inside the h-full column, so the grid
  // area absorbs the height and the page shell never scrolls).
  const [openTaskCount, setOpenTaskCount] = useState(null);
  useEffect(() => {
    if (!facilityId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(
          `/api/facilities/${facilityId}/system-tasks/`,
          { params: { limit: 1 } });
        if (!cancelled) setOpenTaskCount(res.data?.open_total ?? 0);
      } catch { /* footer is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [facilityId]);

  // Unified "new prospect" alert — open leads + no-rental waitlist customers
  // + abandoned rent-wizard drafts. Polled so a fresh signup flashes while
  // the operator is on the dashboard. Dismissed-for-session hides it until
  // a reload (per-facility key so switching facilities re-shows it).
  const [prospects, setProspects] = useState(null);
  const [prospectsDismissed, setProspectsDismissed] = useState(false);
  // "Remind me later" snooze — a wall-clock ms timestamp (per facility,
  // persisted in localStorage) before which the alert stays hidden. Cancel
  // (the ×) is session-only; snooze survives reloads so it stops nagging.
  const [prospectsSnoozeUntil, setProspectsSnoozeUntil] = useState(0);

  // Resolve which layout to show, in priority order:
  //  0. version gate — a layout saved before this DEFAULT_LAYOUT version is
  //     stale; fall through to the base default.
  //  1. the operator's OWN saved layout (user.dashboard_layout / localStorage)
  //  2. the facility's STAFF default layout (set by an owner/manager)
  //  3. the global DEFAULT_LAYOUT
  const resolveLayout = useCallback(() => {
    const facilityDefault =
      Array.isArray(currentFacility?.staff_dashboard_layout)
      && currentFacility.staff_dashboard_layout.length
        ? currentFacility.staff_dashboard_layout
        : null;
    const baseDefault = facilityDefault || DEFAULT_LAYOUT;
    let savedVersion = 0;
    try {
      savedVersion = Number(localStorage.getItem(LS_VERSION_KEY)) || 0;
    } catch (_) {
      savedVersion = 0;
    }
    // Product-default redesign: supersede any pre-version personal layout once.
    if (savedVersion < DASHBOARD_LAYOUT_VERSION) return baseDefault;
    const fromUser = user?.dashboard_layout;
    if (Array.isArray(fromUser) && fromUser.length) return fromUser;
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch (_) {
      // Corrupt LS — fall through to default.
    }
    return baseDefault;
  }, [user?.dashboard_layout, currentFacility?.staff_dashboard_layout]);

  const [layout, setLayout] = useState(resolveLayout);
  const [editMode, setEditMode] = useState(false);
  // Which RGL breakpoint is active. Edits are only persisted at 'lg'.
  const [gridBreakpoint, setGridBreakpoint] = useState('lg');

  // Re-resolve when the facility's staff default or the operator's own saved
  // layout arrives/changes (switching facilities, or /me loading in after
  // mount). Skipped while editing so an in-progress arrangement isn't lost.
  useEffect(() => {
    if (editMode) return;
    setLayout(resolveLayout());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, currentFacility?.staff_dashboard_layout, user?.dashboard_layout]);
  // Add-widget popover open state (edit mode only).
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // Snapshot taken when entering edit mode so Cancel reverts cleanly.
  const editEntrySnapshot = useRef(null);

  // `silent` = background refresh: keep the current numbers on screen (no
  // spinner takeover), and swallow transient failures rather than flashing
  // the error state over data the operator is reading.
  const load = useCallback(async (silent = false) => {
    if (!facilityId) {
      if (!facilityLoading) setLoading(false);
      return;
    }
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [o, r, a, oc] = await Promise.all([
        dashboardService.getOverview(facilityId),
        apiClient
          .get(`/api/facilities/${facilityId}/reports/revenue`)
          .catch(() => ({ data: { by_month: [] } })),
        apiClient
          .get(`/api/facilities/${facilityId}/reports/aging`)
          .catch(() => ({ data: { buckets: {}, rows: [] } })),
        apiClient
          .get(`/api/facilities/${facilityId}/reports/occupancy`)
          .catch(() => ({ data: { by_type: [] } })),
      ]);
      setOv(o.data);
      // /reports/revenue mis-buckets months (it lumped May's $354 into June,
      // showing $5,551 vs the correct $5,197 MTD). The overview's
      // revenue.sparkline is the correct per-month series — prefer it, and
      // only fall back to /reports/revenue if the sparkline is absent.
      const spark = o.data?.revenue?.sparkline;
      setRev6(
        Array.isArray(spark) && spark.length
          ? spark.map((s) => ({ month: s.month, revenue: s.amount })).slice(-6)
          : (r.data.by_month || []).slice(-6),
      );
      setAging(a.data);
      setOccByType(oc.data.by_type || []);
    } catch (e) {
      if (!silent) setError('Unable to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, [facilityId, facilityLoading]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh (owner, 2026-08-13): the dashboard fetched once per mount,
  // so a payment landing while the page sat open (browser tab or the phone
  // app's embedded Home) showed stale numbers until a manual reload. Two
  // triggers: a 15-minute background interval, and an immediate silent
  // refetch whenever the tab/app comes back into view — the moment that
  // actually matters after taking a payment.
  useEffect(() => {
    if (!facilityId) return undefined;
    const id = setInterval(() => load(true), 15 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [facilityId, load]);

  // Pending-prospects poll. Fetch once on facility change, then every ~45s so
  // a NEW lead / waitlist signup / abandoned draft flashes while the operator
  // sits on the dashboard. Reset the session dismissal when the facility
  // changes. Interval is cleared on unmount / facility switch.
  useEffect(() => {
    if (!facilityId) return undefined;
    let active = true;
    setProspectsDismissed(false);
    // Re-read the per-facility "remind me later" snooze for this facility.
    try {
      const raw = localStorage.getItem(`mysms360.prospectSnooze.${facilityId}`);
      setProspectsSnoozeUntil(raw ? Number(raw) || 0 : 0);
    } catch (_) { setProspectsSnoozeUntil(0); }
    const fetchProspects = async () => {
      try {
        const { data } = await dashboardService.getPendingProspects(facilityId);
        if (active) setProspects(data);
      } catch (_) {
        // Banner is non-critical — a failed poll just leaves the last value
        // (or nothing) in place; never surface an error for it.
      }
    };
    fetchProspects();
    const id = setInterval(fetchProspects, 45000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [facilityId]);

  // Edit-mode controls.
  const enterEdit = () => {
    editEntrySnapshot.current = layout;
    setEditMode(true);
  };

  const cancelEdit = () => {
    if (editEntrySnapshot.current) {
      setLayout(editEntrySnapshot.current);
    }
    setAddMenuOpen(false);
    setEditMode(false);
  };

  const saveEdit = async () => {
    // Local-first: persist immediately so a reload survives even before
    // the backend write returns.
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(layout));
      // Stamp the current default-layout version: from here on this operator's
      // own arrangement wins over future loads (until the next version bump).
      localStorage.setItem(LS_VERSION_KEY, String(DASHBOARD_LAYOUT_VERSION));
    } catch (_) {
      // Quota / private-window safe-guard. The PATCH below is the
      // canonical store; LS is convenience only.
    }
    // Fire-and-forget PATCH. Silently swallow 4xx (e.g. backend without
    // the new dashboard_layout field) so Edit mode still works for the
    // operator even if the API isn't deployed yet.
    try {
      await apiClient.patch('/api/auth/me/preferences', {
        dashboard_layout: layout,
      });
    } catch (_) {
      // Ignore — local change is already applied.
    }
    setAddMenuOpen(false);
    setEditMode(false);
  };

  // Owner/manager only: persist the CURRENT arrangement as THIS facility's
  // staff default dashboard layout. Staff who haven't personally customized
  // their dashboard load it (see resolveLayout). Written via the facility
  // update endpoint, which is gated to UPDATE_FACILITY (owner/manager) — staff
  // can't reach it. Also mirrors locally so it sticks for this operator too.
  const [savingDefault, setSavingDefault] = useState(false);
  const saveAsStaffDefault = async () => {
    setSavingDefault(true);
    try {
      await facilitiesService.update(facilityId, {
        staff_dashboard_layout: layout,
      });
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(layout));
        localStorage.setItem(LS_VERSION_KEY, String(DASHBOARD_LAYOUT_VERSION));
      } catch (_) {
        // LS is convenience only; the facility write is canonical.
      }
      setAddMenuOpen(false);
      setEditMode(false);
    } catch (e) {
      alert(
        e?.response?.data?.detail
          || 'Could not save the staff default layout for this facility.',
      );
    } finally {
      setSavingDefault(false);
    }
  };

  const resetEdit = () => {
    // Restore the full default widget set + positions.
    setLayout(DEFAULT_LAYOUT.map((it) => ({ ...it })));
    setAddMenuOpen(false);
  };

  // Remove a widget from the grid (edit mode). Persisted on Save like any
  // other layout change.
  const removeWidget = (id) => {
    setLayout((prev) => prev.filter((it) => it.i !== id));
  };

  // Append a registry widget not currently on the grid, at the bottom using
  // its default size.
  const addWidget = (id) => {
    setLayout((prev) => {
      if (prev.some((it) => it.i === id)) return prev;
      const item = layoutItemFor(id);
      if (!item) return prev;
      // Drop it on a fresh row below everything already placed.
      const maxY = prev.reduce((mx, it) => Math.max(mx, it.y + it.h), 0);
      return [...prev, { ...item, x: 0, y: maxY }];
    });
    setAddMenuOpen(false);
  };

  // Registry widgets not yet placed on the grid (for the Add menu).
  const placedIds = new Set(layout.map((it) => it.i));
  const availableToAdd = WIDGET_REGISTRY.filter((w) => !placedIds.has(w.id));

  if (facilityLoading || loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div
          className="w-5 h-5 rounded-full animate-spin"
          style={{ border: `2px solid ${INK}`, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }
  if (!facilityId) {
    return (
      <div className="text-center py-20">
        <h2 style={{ fontSize: 18, fontWeight: 500, color: INK }}>
          No facility selected
        </h2>
        <p className="mt-1" style={{ fontSize: 13, color: INK3 }}>
          Create one in Settings to get started.
        </p>
      </div>
    );
  }

  const occ = ov?.occupancy || {};
  const rev = ov?.revenue || {};
  const del = ov?.delinquency || {};
  const lead = ov?.leads || {};
  const ai = ov?.ai_agent || ov?.voice_agent || {};
  const attribution = ov?.attribution?.sources || ov?.lead_sources || [];
  // ESS-style operational metrics. move_ins/outs are also on the occupancy
  // snapshot; the operations block is the canonical source for the mix %s.
  const opsMetrics = ov?.operations || {};
  const moveIns = opsMetrics.move_ins_this_month
    ?? occ.move_ins_this_month ?? 0;
  const moveOuts = opsMetrics.move_outs_this_month
    ?? occ.move_outs_this_month ?? 0;
  const waitingList = opsMetrics.waiting_list_count ?? 0;
  const websitePct = opsMetrics.website_rentals_pct ?? 0;
  const recurringPct = opsMetrics.recurring_billing_pct ?? 0;
  const activeRentals = opsMetrics.active_rentals ?? 0;

  const revMonth = rev.mtd_revenue || rev.last_month_revenue || 0;
  const occPct = occ.occupancy_rate ?? 0;
  const totalUnits = occ.total_units || 0;
  const occupied = occ.occupied_units || 0;
  const pastDue = del.total_past_due_balance || 0;
  const pastDueCust = del.total_delinquent_count || 0;
  const leadsMonth = lead.this_month ?? lead.count ?? 0;
  const leadsViaAi = lead.via_ai ?? ai.leads ?? null;

  // The /overview activity feed returns structured EVENTS under
  // `activity.events` ({type, customer_name, unit_number, amount, timestamp}).
  // The widget previously read `activity.items` (wrong key) so it was always
  // empty. Read events and map them to the {text, time, tone, href} shape the
  // Activity widget renders + routes on.
  const rawActivity =
    ov?.activity?.events || ov?.activity?.items || ov?.recent_activity || [];
  const activity = rawActivity.map((a) => {
    if (a.text || a.label || a.message) return a; // already display-shaped
    const typeLabel = {
      move_in: 'Move-in', move_out: 'Move-out',
      payment: 'Payment', payment_applied: 'Payment',
      lead: 'Lead', invoice: 'Invoice',
    }[a.type] || (a.type ? String(a.type).replace(/_/g, ' ') : 'Activity');
    const parts = [typeLabel];
    if (a.customer_name) parts.push(a.customer_name);
    if (a.unit_number) parts.push(`Unit ${a.unit_number}`);
    if (a.amount != null) parts.push(fmtMoney(a.amount));
    const tone = a.type === 'move_out' ? 'danger'
      : a.type === 'move_in' ? 'success'
        : String(a.type || '').includes('payment') ? 'info' : 'tertiary';
    const href = String(a.type || '').includes('move') ? '/rentals'
      : String(a.type || '').includes('payment') ? '/payments'
        : String(a.type || '').includes('lead')
          ? '/reservations?tab=leads' : null;
    // Stored timestamps are naive UTC — render in the facility's zone
    // (raw new Date() showed the UTC clock, e.g. late-evening move-ins
    // stamped with tomorrow's date).
    const time = a.timestamp
      ? formatRowStamp(a.timestamp, currentFacility?.timezone)
      : '';
    return { text: parts.join(' · '), time, tone, href };
  });
  const lastRev = rev6[rev6.length - 1]?.revenue || 0;

  // Map of aging-bucket key to drill aging param value. Backend may emit
  // either "0-30" / "31-60" / "61-90" / "90+" or "0_30" style keys; this
  // covers both.
  const agingParam = (k) => {
    const norm = String(k).replace(/_/g, '-').toLowerCase();
    if (norm.includes('90')) return '90-plus';
    return norm;
  };

  // Clicking a slice (or its legend row) opens the rows behind THAT bucket.
  // Before, every segment fell through to the card-level handler and drilled
  // the whole past-due list, so the answer to "who is 61-90 days late?" was
  // the same list as "who owes anything?".
  const agingDrillMetric = (bucket) => {
    // Map the RAW key. `agingParam` cannot be reused here: it returns
    // '90-plus' for anything containing "90", so "61-90" resolved to the 90+
    // bucket and clicking 61-90 opened an empty 90+ list.
    const raw = String(bucket?.key ?? bucket).replace(/_/g, '-').toLowerCase();
    const key = raw === '90+' ? '90-plus' : raw;
    const map = {
      'not-yet-due': 'aging_not_yet_due',
      '0-30': 'aging_0_30',
      '31-60': 'aging_31_60',
      '61-90': 'aging_61_90',
      '90-plus': 'aging_90_plus',
    };
    return map[key] || null;
  };
  const onAgingSegment = (bucket) => {
    const metric = agingDrillMetric(bucket);
    if (metric) setDrillMetric(metric);
  };

  // Unified "new prospect" flashing alert. Renders only when there is at
  // least one prospect needing attention AND the operator hasn't dismissed
  // it this session AND we're not in edit mode (keep it out of the way while
  // rearranging widgets). Deep-links to the single source's page when the
  // pool is homogeneous, else to lead management as the catch-all hub.
  // The banner NAMES nobody and links ONE place. It used to list names and
  // deep-link per source, which meant an operator clicking through landed on
  // a customer page — or on Lead Management, which showed none of them,
  // because waitlist and incomplete signups never appear in `leads`. A
  // pointer that can send you somewhere the thing isn't is worse than a
  // pointer that always sends you to the inbox.
  const prospectCount = prospects?.count || 0;
  const prospectLink = '/reservations?tab=leads';
  // "Remind me later" snoozes the alert for 4h (persisted per facility); the
  // × cancels it for the session. Both stop it from nagging.
  const prospectSnoozed =
    prospectsSnoozeUntil > 0 && Date.now() < prospectsSnoozeUntil;
  const remindLater = () => {
    const until = Date.now() + 4 * 60 * 60 * 1000;
    try {
      localStorage.setItem(`mysms360.prospectSnooze.${facilityId}`, String(until));
    } catch (_) { /* private mode — best effort */ }
    setProspectsSnoozeUntil(until);
  };
  const prospectBanner =
    !editMode && prospectCount > 0 && !prospectsDismissed && !prospectSnoozed ? (
      <div
        role="alert"
        aria-live="polite"
        className="inline-flex items-center gap-1.5 flex-shrink-0 animate-pulse"
        style={{
          background: 'rgba(186,117,23,0.10)',
          border: `0.5px solid ${WARNING}`,
          borderRadius: 999,
          padding: '4px 6px 4px 10px',
          maxWidth: 460,
        }}
      >
        <Link
          to={prospectLink}
          className="flex items-center gap-1.5 min-w-0"
          style={{ fontSize: 12, color: INK, textDecoration: 'none' }}
        >
          <span aria-hidden="true">🔔</span>
          <span className="truncate">
            <span style={{ fontWeight: 500 }}>
              {prospectCount} lead{prospectCount === 1 ? '' : 's'} waiting
            </span>
            <span style={{ color: INK2 }}>{' — check Lead Management'}</span>
          </span>
        </Link>
        <button
          onClick={remindLater}
          className="rounded-full px-2 py-0.5 transition-colors hover:bg-amber-100"
          style={{ fontSize: 10, color: INK2, flexShrink: 0, whiteSpace: 'nowrap' }}
          aria-label="Remind me later"
          title="Hide for 4 hours"
        >
          Later
        </button>
        <button
          onClick={() => setProspectsDismissed(true)}
          className="flex items-center justify-center rounded-full transition-colors hover:bg-amber-100"
          style={{ width: 18, height: 18, color: INK2, flexShrink: 0 }}
          aria-label="Cancel prospect alert for this session"
          title="Cancel (this session)"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    ) : null;

  // Edit-mode hint band.
  const editBand = editMode && (
    <div
      className="flex items-center gap-3 flex-shrink-0"
      style={{
        background: 'rgba(59,130,246,0.08)',
        border: `0.5px solid ${BORDER}`,
        borderRadius: 8,
        padding: '8px 12px',
      }}
    >
      <GripVertical className="h-3.5 w-3.5" style={{ color: INK3 }} />
      <span style={{ fontSize: 12, color: INK2 }}>
        Drag to rearrange · resize from the corner · × to remove.
      </span>
      <div className="flex-1" />
      {/* Add-widget popover. Lists registry widgets NOT on the grid;
          already-placed ones are greyed + disabled. */}
      <div className="relative">
        <button
          onClick={() => setAddMenuOpen((o) => !o)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors"
          style={{ fontSize: 12, color: INK2, background: SURFACE,
            border: `0.5px solid ${BORDER}` }}
          aria-haspopup="menu"
          aria-expanded={addMenuOpen}
        >
          <Plus className="h-3 w-3" /> Add widget
        </button>
        {addMenuOpen && (
          <div
            role="menu"
            className="absolute right-0 z-20 mt-1.5 overflow-y-auto sidebar-scroll"
            style={{ top: '100%', width: 244, maxHeight: 320,
              background: SURFACE, border: `0.5px solid ${BORDER}`,
              borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              padding: 6 }}
          >
            <p style={{ fontSize: 10, color: INK3, padding: '4px 8px' }}>
              {availableToAdd.length > 0
                ? 'Add a widget' : 'All widgets are on the dashboard'}
            </p>
            {WIDGET_REGISTRY.map((w) => {
              const placed = placedIds.has(w.id);
              return (
                <button
                  key={w.id}
                  role="menuitem"
                  disabled={placed}
                  onClick={() => !placed && addWidget(w.id)}
                  className="flex items-center gap-2 w-full text-left rounded-md transition-colors"
                  style={{ fontSize: 12, padding: '7px 8px',
                    color: placed ? INK3 : INK,
                    cursor: placed ? 'default' : 'pointer',
                    opacity: placed ? 0.55 : 1,
                    background: 'transparent' }}
                >
                  {placed
                    ? <Check className="h-3 w-3 shrink-0" style={{ color: INK3 }} />
                    : <Plus className="h-3 w-3 shrink-0" style={{ color: INK2 }} />}
                  <span className="flex-1 truncate">{w.label}</span>
                  {placed && (
                    <span style={{ fontSize: 9, color: INK3 }}>added</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <button
        onClick={resetEdit}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors"
        style={{ fontSize: 12, color: INK2, background: SURFACE,
          border: `0.5px solid ${BORDER}` }}
      >
        <RotateCcw className="h-3 w-3" /> Reset
      </button>
      <button
        onClick={cancelEdit}
        className="px-3 py-1.5 rounded-full transition-colors"
        style={{ fontSize: 12, color: INK2, background: SURFACE,
          border: `0.5px solid ${BORDER}` }}
      >
        Cancel
      </button>
      {/* Owner/manager only: make THIS arrangement the facility-wide staff
          default. Staff who haven't customized their own dashboard load it. */}
      {isManager && isManager() && (
        <button
          onClick={saveAsStaffDefault}
          disabled={savingDefault}
          title="Make this the default dashboard layout for staff at this facility"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors disabled:opacity-60"
          style={{ fontSize: 12, color: INK2, background: SURFACE,
            border: `0.5px solid ${BORDER}` }}
        >
          <Check className="h-3 w-3" />
          {savingDefault ? 'Saving…' : 'Save as staff default'}
        </button>
      )}
      <button
        onClick={saveEdit}
        className="px-3 py-1.5 rounded-full transition-colors"
        style={{ fontSize: 12, color: 'white', background: INK,
          border: `0.5px solid ${INK}` }}
      >
        Save layout
      </button>
    </div>
  );

  // Widget renderers — keyed off the same `i` values as DEFAULT_LAYOUT.
  // Drill-down disabled in edit mode so clicks don't navigate while the
  // operator is rearranging.
  const widgets = {
    'kpi-revenue': (
      <Metric
        label="Revenue this month"
        value={fmtMoney(revMonth)}
        deltaPct={rev.mom_delta_pct}
        deltaTone={rev.mom_delta_pct >= 0 ? 'success' : 'danger'}
        sub="vs last month"
        onClick={editMode ? null : () => setDrillMetric('revenue')}
        ariaLabel="Show the payments behind this month's revenue"
      />
    ),
    'kpi-occupancy': (
      <Metric
        label="Occupancy"
        value={`${occPct}%`}
        deltaPct={occ.delta_rate_vs_last_month}
        deltaTone={
          (occ.delta_rate_vs_last_month ?? 0) >= 0 ? 'success' : 'danger'}
        sub={`${occupied} of ${totalUnits} filled`}
        onClick={editMode ? null : () => setDrillMetric('occupancy')}
        ariaLabel="Show the occupied vs available unit breakdown"
      />
    ),
    'kpi-past-due': (
      <Metric
        label="Past due"
        value={fmtMoney(pastDue)}
        sub={
          pastDueCust
            ? `${pastDueCust} tenant${pastDueCust > 1 ? 's' : ''}`
            : 'none'
        }
        onClick={editMode ? null : () => setDrillMetric('past_due')}
        ariaLabel="Show the tenants behind the past-due balance"
      />
    ),
    'kpi-leads': (
      <Metric
        label="Leads this month"
        value={String(leadsMonth)}
        sub={
          leadsViaAi != null
            ? `${leadsViaAi} via AI`
            : (lead.converted_pct != null
              ? `${lead.converted_pct}% converted` : null)
        }
        onClick={editMode ? null : () => setDrillMetric('leads')}
        ariaLabel="Show the leads counted this month"
      />
    ),
    // — New ESS-style operational KPI tiles —
    'kpi-move-ins': (
      <Metric
        label="Move-ins this month"
        value={String(moveIns)}
        sub="new rentals"
        onClick={editMode ? null : () => setDrillMetric('move_ins')}
        ariaLabel="Show this month's move-in rentals"
      />
    ),
    'kpi-move-outs': (
      <Metric
        label="Move-outs this month"
        value={String(moveOuts)}
        sub="ended rentals"
        onClick={editMode ? null : () => setDrillMetric('move_outs')}
        ariaLabel="Show this month's move-out rentals"
      />
    ),
    'kpi-waiting-list': (
      <Metric
        label="Waiting list"
        value={String(waitingList)}
        sub={waitingList === 1 ? 'contact' : 'contacts'}
        to={editMode ? null : '/customers?status=waiting_list'}
        ariaLabel="Show customers on the waiting list"
      />
    ),
    'kpi-website-rentals': (
      <Metric
        label="Website rentals"
        value={`${websitePct}%`}
        sub="of active rentals"
        onClick={editMode ? null : () => setDrillMetric('website_rentals')}
        ariaLabel="Share of active rentals that originated on the website"
      />
    ),
    'kpi-recurring-billing': (
      <Metric
        label="Recurring billing"
        value={`${recurringPct}%`}
        sub="on autopay"
        onClick={editMode ? null : () => setDrillMetric('recurring_billing')}
        ariaLabel="Share of active rentals enrolled in autopay"
      />
    ),
    'kpi-active-rentals': (
      <Metric
        label="Active rentals"
        value={String(activeRentals)}
        sub="in good standing"
        onClick={editMode ? null : () => setDrillMetric('active_rentals')}
        ariaLabel="Show the active rentals behind this count"
      />
    ),
    // — Charts as first-class grid widgets (self-fetching, fill the cell) —
    'chart-revenue-bar': (
      <RevenueBarWidget
        facilityId={facilityId}
        onClick={editMode ? undefined : () => setDrillMetric('revenue')}
        onBarClick={editMode ? undefined : (bar) => {
          // A bar click opens THAT month's revenue, not always the latest.
          if (bar?.month) setDrillMonth(bar.month);
          setDrillMetric('revenue');
        }}
      />
    ),
    'chart-aging-donut': (
      <AgingDonutWidget
        facilityId={facilityId}
        revenue={revMonth}
        onClick={editMode ? undefined : () => setDrillMetric('past_due')}
        onAgingSegment={editMode ? undefined : onAgingSegment}
        onMadeClick={editMode ? undefined : () => setDrillMetric('revenue')}
        onDueClick={editMode ? undefined : () => setDrillMetric('past_due')}
      />
    ),
    'chart-occupancy-gauge': (
      <OccupancyGaugeWidget
        facilityId={facilityId}
        occupancy={occ}
        onDrill={editMode ? undefined : () => setDrillMetric('occupancy')}
      />
    ),
    // — Key Metrics donut (unit-status mix). Reuses the already-loaded
    //   occupancy snapshot; each slice drills the units of that status. —
    'chart-key-metrics': (
      <KeyMetricsWidget
        facilityId={facilityId}
        occupancy={occ}
        onSliceClick={editMode ? undefined : () => setDrillMetric('unit_status')}
      />
    ),
    // — Occupancy trend (reconstructed % over ~12 month-ends from real
    //   rental dates). Whole-card click opens the occupancy breakdown. —
    'chart-occupancy-trend': (
      <OccupancyTrendWidget
        facilityId={facilityId}
        onClick={editMode ? undefined : () => setDrillMetric('occupancy')}
        moveIns={moveIns}
        moveOuts={moveOuts}
        onMoveInClick={editMode ? undefined : () => setDrillMetric('move_ins')}
        onMoveOutClick={editMode ? undefined : () => setDrillMetric('move_outs')}
      />
    ),
    // — Standalone move-ins / move-outs KPI pair (the green/red chips pulled
    //   out of the Activity card into their own grid widget). Each chip drills
    //   into the move-in / move-out rentals for the month. —
    'kpi-move-inout': (
      <Card title="Move-ins / Move-outs" chip="this month">
        {/* Stacked vertically so the pair reads cleanly in a narrow tile
            (it sits in the second row beside the trend charts). */}
        <div
          className="flex flex-col justify-center gap-2 h-full"
          style={tabular}
        >
          <Drill
            onClick={editMode ? null : () => setDrillMetric('move_ins')}
            ariaLabel="Show this month's move-in rentals"
            className="flex items-baseline justify-center gap-1.5 rounded-lg w-full"
            style={{ background: 'var(--c-success-bg)', padding: '8px 12px' }}
          >
            <span style={{ fontSize: 24, fontWeight: 600, color: SUCCESS, lineHeight: 1 }}>
              {moveIns}
            </span>
            <span style={{ fontSize: 10, fontWeight: 500, color: SUCCESS, opacity: 0.85 }}>
              move-ins
            </span>
          </Drill>
          <Drill
            onClick={editMode ? null : () => setDrillMetric('move_outs')}
            ariaLabel="Show this month's move-out rentals"
            className="flex items-baseline justify-center gap-1.5 rounded-lg w-full"
            style={{ background: 'var(--c-danger-bg)', padding: '8px 12px' }}
          >
            <span style={{ fontSize: 24, fontWeight: 600, color: DANGER, lineHeight: 1 }}>
              {moveOuts}
            </span>
            <span style={{ fontSize: 10, fontWeight: 500, color: DANGER, opacity: 0.85 }}>
              move-outs
            </span>
          </Drill>
        </div>
      </Card>
    ),
    'revenue-chart': (
      <Card title="Revenue" chip="6 months" className="overflow-hidden">
        {rev6.length > 0 ? (
          <div className="relative h-full min-h-[120px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={rev6}
                margin={{ top: 22, right: 44, left: 0, bottom: 0 }}
                onClick={(e) => {
                  if (editMode) return;
                  const m = e?.activePayload?.[0]?.payload?.month;
                  if (m) navigate(`/payments?month=${encodeURIComponent(m)}`);
                }}>
                <defs>
                  <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--c-ink)"
                      stopOpacity={0.08} />
                    <stop offset="100%" stopColor="var(--c-ink)"
                      stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tickLine={false}
                  axisLine={false} tickMargin={10}
                  tick={{ fill: INK3, fontSize: 10 }} />
                <Tooltip {...TT} />
                <Area type="monotone" dataKey="revenue"
                  stroke={CHART_LINE} strokeWidth={1.5}
                  fill="url(#revFill)" dot={false}
                  activeDot={{ r: 4, fill: 'var(--c-ink)', strokeWidth: 0,
                    style: { cursor: editMode ? 'default' : 'pointer' } }} />
              </AreaChart>
            </ResponsiveContainer>
            <span
              className="absolute right-4"
              style={{ top: 2, fontSize: 10, fontWeight: 500,
                color: INK, ...tabular }}
            >
              {fmtMoney(lastRev)}
            </span>
          </div>
        ) : (
          <div
            className="flex items-center justify-center"
            style={{ height: 150, fontSize: 12, color: INK3 }}
          >
            No revenue yet
          </div>
        )}
      </Card>
    ),
    'activity': (
      <Card title="Activity">
        {/* Move-in / move-out tally for the month, above the event feed.
            Each chip drills into the specific rentals behind it (the same
            breakdown the move-in/out KPI tiles open). Inert in edit mode. */}
        <div
          className="flex items-center gap-2.5 flex-shrink-0"
          style={{ marginBottom: 10, paddingBottom: 10,
            borderBottom: `0.5px solid ${BORDER}`, ...tabular }}
        >
          <Drill
            onClick={editMode ? null : () => setDrillMetric('move_ins')}
            ariaLabel="Show this month's move-in rentals"
            className="flex items-baseline gap-1.5 rounded-lg"
            style={{ background: 'var(--c-success-bg)', padding: '4px 10px' }}
          >
            <span style={{ fontSize: 20, fontWeight: 600, color: SUCCESS, lineHeight: 1 }}>
              {moveIns}
            </span>
            <span style={{ fontSize: 10, fontWeight: 500, color: SUCCESS, opacity: 0.85 }}>
              move-ins
            </span>
          </Drill>
          <Drill
            onClick={editMode ? null : () => setDrillMetric('move_outs')}
            ariaLabel="Show this month's move-out rentals"
            className="flex items-baseline gap-1.5 rounded-lg"
            style={{ background: 'var(--c-danger-bg)', padding: '4px 10px' }}
          >
            <span style={{ fontSize: 20, fontWeight: 600, color: DANGER, lineHeight: 1 }}>
              {moveOuts}
            </span>
            <span style={{ fontSize: 10, fontWeight: 500, color: DANGER, opacity: 0.85 }}>
              move-outs
            </span>
          </Drill>
          <span style={{ fontSize: 10, color: INK3 }}>this month</span>
        </div>
        {activity.length > 0 ? (
          <ul>
            {activity.slice(0, 6).map((a, i) => {
              const route = editMode ? null : activityRoute(a);
              const row = (
                <li
                  className="flex items-start gap-2.5"
                  style={{
                    paddingBlock: 8,
                    borderTop: i === 0 ? 'none'
                      : `0.5px solid ${BORDER}`,
                  }}
                >
                  <span
                    className="rounded-full shrink-0"
                    style={{ marginTop: 5, height: 6, width: 6,
                      background: DOT[a.tone] || DOT.tertiary }}
                  />
                  <span
                    className="flex-1 leading-snug"
                    style={{ fontSize: 12, color: INK }}
                  >
                    {a.text || a.label || a.message}
                  </span>
                  <span
                    className="shrink-0"
                    style={{ fontSize: 10, color: INK3 }}
                  >
                    {a.time || a.when || ''}
                  </span>
                </li>
              );
              if (!route) return <React.Fragment key={i}>{row}</React.Fragment>;
              return (
                <Link
                  key={i}
                  to={route}
                  aria-label={`Open ${a.text || a.label || 'activity item'}`}
                  className="block cursor-pointer transition-colors hover:bg-slate-50"
                >
                  {row}
                </Link>
              );
            })}
          </ul>
        ) : (
          <p
            className="text-center"
            style={{ fontSize: 12, color: INK3, paddingBlock: 40 }}
          >
            No recent activity
          </p>
        )}
      </Card>
    ),
    'occupancy-breakdown': (
      <Card title="Occupancy breakdown">
        {occByType.length > 0 ? (
          <>
            {/* Vertical (column) bars — thicker than the old horizontal rows
                to use the tile real estate. Each column drills into the Units
                grid for that type. pct on top, type label beneath. */}
            <div className="flex items-stretch gap-2 h-full" style={{ minHeight: 120 }}>
              {occByType.map((t, i) => {
                const slug = String(t.unit_type || '').toLowerCase();
                // Heuristic: bare size strings (e.g. "10x10") get
                // ?size=...; word strings (e.g. "Warehouse") get ?type=...
                const isSize = /^\d+x\d+$/i.test(slug);
                const to = editMode
                  ? null
                  : (isSize
                    ? `/units?size=${encodeURIComponent(slug)}&view=grid`
                    : `/units?type=${encodeURIComponent(slug)}&view=grid`);
                const pct = Math.round(t.occupancy_pct ?? 0);
                const color = TYPE_COLORS[i % TYPE_COLORS.length];
                return (
                  <Drill
                    key={t.unit_type}
                    to={to}
                    ariaLabel={`Filter ${t.unit_type} units`}
                    className="flex-1 min-w-0 rounded-md"
                  >
                    <div className="flex flex-col items-center h-full" style={tabular}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: INK, lineHeight: 1 }}>
                        {pct}%
                      </span>
                      <div
                        className="flex-1 w-full flex items-end justify-center"
                        style={{ minHeight: 0, marginTop: 4 }}
                      >
                        <div
                          style={{
                            width: '72%', maxWidth: 46,
                            height: `${Math.max(pct, 2)}%`,
                            background: color,
                            borderRadius: '5px 5px 0 0',
                          }}
                        />
                      </div>
                      <span
                        className="truncate w-full text-center"
                        style={{ fontSize: 10, color: INK3, marginTop: 6 }}
                        title={t.unit_type}
                      >
                        {t.unit_type}
                      </span>
                    </div>
                  </Drill>
                );
              })}
            </div>
          </>
        ) : (
          <p
            className="text-center"
            style={{ fontSize: 12, color: INK3, paddingBlock: 24 }}
          >
            No unit data
          </p>
        )}
      </Card>
    ),
    'attribution': (
      <Card title="Attribution" chip="this month">
        {attribution.length > 0 ? (
          <>
            {attribution.map((s) => (
              <BarRow
                key={s.source}
                label={s.source}
                pct={s.pct ?? s.share_pct ?? 0}
                fill={INK2}
                labelW={65}
                trail={`${s.leads ?? 0} · ${s.move_ins ?? 0} in`}
                to={editMode
                  ? null
                  : `/reservations?tab=leads&source=${encodeURIComponent(s.source)}`}
                ariaLabel={`Drill into ${s.source} leads`}
              />
            ))}
            {ov?.attribution?.cost_per_move_in != null && (
              <p
                style={{ marginTop: 10, fontSize: 10, color: INK3 }}
              >
                {fmtMoney(ov.attribution.cost_per_move_in)} per move-in
              </p>
            )}
          </>
        ) : aging?.buckets &&
          Object.values(aging.buckets).some((v) => v > 0) ? (
          <>
            {Object.entries(aging.buckets).map(([k, v]) => {
              const total = aging.total || 1;
              return (
                <BarRow
                  key={k}
                  label={`${k}d`}
                  pct={Math.round((v / total) * 100)}
                  fill={DANGER}
                  labelW={65}
                  trail={fmtMoney(v)}
                  to={editMode
                    ? null
                    : `/customers?status=delinquent&aging=${agingParam(k)}`}
                  ariaLabel={`Drill into ${k} days past due`}
                />
              );
            })}
          </>
        ) : (
          <p
            className="text-center"
            style={{ fontSize: 12, color: INK3, paddingBlock: 24 }}
          >
            No attribution data
          </p>
        )}
      </Card>
    ),
    'ai-agent': (
      <Card title="AI agent" chip="30 days">
        <div>
          <KpiRow k="Answered" v={`${ai.calls_answered ?? 0} calls`}
            to={editMode ? null : '/intelligence'}
            ariaLabel="Open AI Voice Agent dashboard" />
          <KpiRow
            k="Leads"
            v={`${ai.leads ?? 0}${ai.lead_pct != null
              ? ` (${ai.lead_pct}%)` : ''}`}
            to={editMode ? null : '/intelligence'}
            ariaLabel="Open AI Voice Agent leads"
          />
          <KpiRow
            k="Reserved"
            v={`${ai.reservations ?? 0}${ai.reserved_pct != null
              ? ` (${ai.reserved_pct}%)` : ''}`}
            to={editMode ? null : '/intelligence'}
            ariaLabel="Open AI Voice Agent reservations"
          />
          <KpiRow k="Avg duration" v={ai.avg_duration || '—'}
            to={editMode ? null : '/intelligence'}
            ariaLabel="Open AI Voice Agent metrics" />
          <KpiRow k="Agent cost" v={fmtMoney(ai.cost ?? 0)}
            to={editMode ? null : '/intelligence'}
            ariaLabel="Open AI Voice Agent costs" />
        </div>
        <div
          style={{ marginTop: 12, paddingTop: 12,
            borderTop: `0.5px solid ${BORDER}` }}
        >
          <p
            style={{ fontSize: 28, fontWeight: 500, color: SUCCESS,
              lineHeight: 1, ...tabular }}
          >
            {ai.roi_multiple != null ? `${ai.roi_multiple}x` : '—'}
          </p>
          <p style={{ marginTop: 4, fontSize: 10, color: INK3 }}>
            Return on AI
          </p>
          {ai.cost != null && ai.value_generated != null && (
            <p style={{ fontSize: 10, color: INK3 }}>
              {fmtMoney(ai.cost)} in → {fmtMoney(ai.value_generated)} out
            </p>
          )}
        </div>
      </Card>
    ),
  };

  return (
    <div className="h-full flex flex-col gap-2 pt-0 pb-1 min-h-0">
      {/* Edit toolbar. The prospect pill sits inline on the left so it never
          adds a row / pushes the grid down. */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {prospectBanner}
        <div className="flex-1" />
        {!editMode && (
          <button
            onClick={enterEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors hover:bg-slate-50"
            style={{ fontSize: 12, color: INK2, background: SURFACE,
              border: `0.5px solid ${BORDER}` }}
            aria-label="Edit dashboard layout"
          >
            <Pencil className="h-3 w-3" /> Edit dashboard
          </button>
        )}
      </div>

      {editBand}

      {error && (
        <div
          style={{ borderRadius: 8, fontSize: 13, padding: '10px 16px',
            color: WARNING, background: 'rgba(186,117,23,0.10)' }}
        >
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        <ResponsiveGridLayout
          className={`layout ${editMode ? 'rgl-edit-mode' : ''}`}
          // One layout per breakpoint. lg is the operator's saved arrangement;
          // md/sm are derived stacks so nothing is ever crushed to ~100px.
          layouts={{
            lg: layout,
            md: stackLayout(layout, 6),
            sm: stackLayout(layout, 1),
          }}
          breakpoints={GRID_BREAKPOINTS}
          cols={GRID_COLS}
          rowHeight={36}
          margin={[16, 16]}
          containerPadding={[0, 0]}
          isDraggable={editMode && gridBreakpoint === 'lg'}
          isResizable={editMode && gridBreakpoint === 'lg'}
          onBreakpointChange={(bp) => setGridBreakpoint(bp)}
          onLayoutChange={(next, allLayouts) => {
            // Persist ONLY the 12-col arrangement. md/sm are derived stacks;
            // saving one would replace the operator's real layout.
            if (gridBreakpoint === 'lg' && allLayouts?.lg) setLayout(allLayouts.lg);
          }}
          // Clicks on the per-widget remove button must never start a drag.
          draggableCancel=".rgl-no-drag"
          // Re-render keyed on editMode so isDraggable/isResizable
          // actually flip without stale internal state.
          useCSSTransforms={true}
        >
          {/* Guard: skip any saved-layout id with no renderer (old layouts
              referencing a removed widget) so the grid never crashes. */}
          {layout
            .filter((item) => widgets[item.i] !== undefined)
            .map((item) => (
              <div key={item.i} data-grid={item}>
                {widgets[item.i]}
                {editMode && (
                  <>
                    <div
                      className="absolute top-2 right-8 pointer-events-none z-10"
                      style={{ color: INK3 }}
                      aria-hidden="true"
                    >
                      <GripVertical className="h-3.5 w-3.5" />
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeWidget(item.i);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="rgl-no-drag absolute top-1.5 right-1.5 z-10 flex items-center justify-center rounded-full transition-colors hover:bg-slate-100"
                      style={{ width: 20, height: 20, color: INK3,
                        background: SURFACE, border: `0.5px solid ${BORDER}` }}
                      aria-label={`Remove ${WIDGET_BY_ID[item.i]?.label || item.i} widget`}
                      title="Remove widget"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            ))}
        </ResponsiveGridLayout>
      </div>

      {/* Slim task-list footer — one line, flex-shrink-0, so the page
          shell stays exactly as tall as the viewport (no scroll). */}
      <Link
        to="/tasks"
        className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200/80 bg-white text-[12px] text-red-600 hover:text-red-700 hover:border-red-200 transition-colors"
      >
        <ClipboardList className="h-3.5 w-3.5" />
        <span className={openTaskCount > 0 ? 'font-bold animate-pulse' : ''}>
          {openTaskCount === null
            ? 'Tasks'
            : `${openTaskCount} open task${openTaskCount === 1 ? '' : 's'}`}
        </span>
        <span className="ml-auto font-medium">View →</span>
      </Link>

      {drillMetric && (
        <MetricDrillDown
          facilityId={facilityId}
          metric={drillMetric}
          month={drillMonth}
          onClose={() => { setDrillMetric(null); setDrillMonth(null); }}
        />
      )}
    </div>
  );
};

export default Dashboard;
